import {
  AnalyticsFieldName,
  ImageCompressionSource,
  SMTAnalyticsSource,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import {
  AcceptedDocumentMimeTypes,
  DocumentMimeType,
} from "@/constants/Documents";
import {
  SMTFallbackUIConfig,
  SMTImageCompressionOption,
} from "@/constants/SMT";
import { ConfirmationAlertTestID, TextTestID } from "@/constants/TestID";
import useImageCompression from "@/hooks/useImageCompression";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import PickFromCameraButton from "@/ui/composed/pickers/PickFromCameraButton";
import PickFromGalleryButton from "@/ui/composed/pickers/PickFromGalleryButton";
import { Button, FAIconName, Icon, Text } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface SMTImagePickerModalProps {
  isOpen: boolean;
  onAssetPickerLaunch: () => void;
  onAssetSelected: (asset: FilePickerAsset) => void;
  onDismiss: () => void;
  onSkipPhoto: () => void;
  uiConfig?: SMTAddPhotoUIConfig;
}

const TestID = "smt-image-picker-modal";

const SMTImagePickerModal = ({
  isOpen,
  onAssetPickerLaunch,
  onAssetSelected,
  onDismiss,
  onSkipPhoto,
  uiConfig = SMTFallbackUIConfig.addFood.addPhoto,
}: SMTImagePickerModalProps) => {
  const baseTestID = joinTestIDs(TestID, ConfirmationAlertTestID.Primary);
  const { track } = useAnalytics();
  const { compressImage } = useImageCompression();
  const { showToast } = useToast();

  const buttonLabels = uiConfig.buttons;

  const handleAssetPicked = async (originalAsset: FilePickerAsset) => {
    const { mimeType } = originalAsset;
    // if expo-image-picker allows user to pick a file type not supported by file-service, it'll fail in a unspecific 500 error
    // so we filter here to ensure that the file type is in fact supported by file-service
    const isUnsupportedMimeType =
      !AcceptedDocumentMimeTypes.MealImageUpload.some(
        (acceptedMimeType) => acceptedMimeType === mimeType,
      );
    // on mobile, TIFF is viewable, and SMT upload converts to JPG successfully
    // on web, non-Safari browsers can't view TIFF, and SMT upload can't convert to JPG (needs a new converter library)
    const isUnsupportedOnWeb = isWeb() && mimeType === DocumentMimeType.TIFF;
    if (isUnsupportedMimeType || isUnsupportedOnWeb) {
      showToast({
        text: `${mimeType} image uploads aren't supported${isWeb() ? " on web" : ""}. Please choose a different photo.`,
        variant: "error",
      });
      return;
    }

    const compressedImage = await compressImage({
      compressionRate: SMTImageCompressionOption.CompressionRate,
      format: SMTImageCompressionOption.Format,
      originalAsset,
      source: ImageCompressionSource.SMT,
    });

    onAssetSelected(compressedImage);
  };

  const handleCameraPress = () => {
    track(SMTEntryAnalyticsEvent.TakePhotoPressed, {
      [AnalyticsFieldName.Source]: SMTAnalyticsSource.ImagePickerModal,
    });
    onAssetPickerLaunch();
    onDismiss();
  };

  const handleGalleryPress = () => {
    track(SMTEntryAnalyticsEvent.ChooseFromLibraryPressed, {
      [AnalyticsFieldName.Source]: SMTAnalyticsSource.ImagePickerModal,
    });
    onAssetPickerLaunch();
    onDismiss();
  };

  const handleSkipPhoto = () => {
    track(SMTEntryAnalyticsEvent.ContinueWithoutPhotoPressed, {
      [AnalyticsFieldName.Source]: SMTAnalyticsSource.ImagePickerModal,
    });
    onSkipPhoto();
    onDismiss();
  };

  const handleModalDismiss = () => {
    track(SMTEntryAnalyticsEvent.AddPhotoDismissed, {
      [AnalyticsFieldName.Source]: SMTAnalyticsSource.ImagePickerModal,
    });
    onDismiss();
  };

  return (
    <BottomSheetModal
      dismissAccessibilityLabel="Dismiss SMT Image Picker Modal"
      icon={
        <Icon
          color={ColorToken.AccentPeriwinkle}
          name={FAIconName.Salad}
          testID={joinTestIDs(baseTestID, "title")}
        />
      }
      isOpen={isOpen}
      onRequestClose={handleModalDismiss}
      testID={baseTestID}
      title={uiConfig.title}
    >
      <VStack gap={18}>
        <Text
          testID={joinTestIDs(baseTestID, TextTestID.Description)}
          variant="body"
        >
          {uiConfig.description}
        </Text>
        <VStack alignItems="center" gap={12} justifyContent="center">
          <Button
            isFullWidth
            leftIcon={FAIconName.ArrowRight}
            onPress={handleSkipPhoto}
            testID={joinTestIDs(baseTestID, "skip-photo")}
            title={buttonLabels.noPhoto}
            variant="secondary"
          />
          <PickFromGalleryButton
            onAssetPicked={handleAssetPicked}
            onPress={handleGalleryPress}
            testID={baseTestID}
            title={buttonLabels.selectPhoto}
            variant="secondary"
          />
          <PickFromCameraButton
            onAssetPicked={handleAssetPicked}
            onPress={handleCameraPress}
            testID={baseTestID}
            title={buttonLabels.takePhoto}
          />
        </VStack>
      </VStack>
    </BottomSheetModal>
  );
};

export type { SMTImagePickerModalProps };
export default SMTImagePickerModal;
